import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Group, Badge, Skeleton } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { useUsers } from '../../../../data/useUsers';
import { useLedger } from '../../../../data/useLedger';
import { useAuditFeed, AUDIT_SOURCES } from '../../../../data/useAuditFeed';
import type { admin as adminNs } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative, formatMinor, formatInteger } from '../../../../lib/format';
import { StatCard } from '../../../../components/StatCard';
import { ImpersonationsCard } from './ImpersonationsCard';
import { AgreementsCard } from './AgreementsCard';
import { useT } from '../../../../i18n/I18nProvider';

type Tenant = NonNullable<Awaited<ReturnType<typeof adminNs.listTenants>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

export function OverviewTab({ tenant }: { tenant: Tenant }) {
  const t = useT();
  const since = useMemo(() => new Date(Date.now() - 30 * 86_400_000).toISOString(), []);
  const enabled = useMemo(
    () => Object.fromEntries(AUDIT_SOURCES.map((s) => [s, true])) as Record<typeof AUDIT_SOURCES[number], boolean>,
    []
  );

  const { data: usersData, isLoading: usersLoading } = useUsers();
  const { data: ledgerData, isLoading: ledgerLoading } = useLedger({ org_id: tenant.id, limit: 200 });
  const { rows: auditRows, loading: auditLoading } = useAuditFeed(since, enabled, 200);
  const { data: integrationsData, isLoading: integrationsLoading } = useQuery({
    queryKey: ['admin', 'integrations', 'org', tenant.id],
    queryFn: () => admin.listIntegrations({ scope: 'org', org_id: tenant.id })
  });

  const userCount = useMemo(
    () => (usersData?.data ?? []).filter((u) => u.organisation_id === tenant.id).length,
    [usersData, tenant.id]
  );
  const platformAdmins = useMemo(
    () => (usersData?.data ?? []).filter((u) => u.organisation_id === tenant.id && u.scope === 'platform').length,
    [usersData, tenant.id]
  );
  const ledger = ledgerData?.data ?? [];
  const debits30d = useMemo(
    () =>
      ledger
        .filter((e) => new Date(e.occurred_at).getTime() >= Date.now() - 30 * 86_400_000)
        .filter((e) => e.amount_minor < 0)
        .reduce((s, e) => s + Math.abs(e.amount_minor), 0),
    [ledger]
  );
  const credits30d = useMemo(
    () =>
      ledger
        .filter((e) => new Date(e.occurred_at).getTime() >= Date.now() - 30 * 86_400_000)
        .filter((e) => e.amount_minor > 0)
        .reduce((s, e) => s + e.amount_minor, 0),
    [ledger]
  );
  const currency = ledger[0]?.currency ?? 'ZAR';
  const recentActivity = useMemo(
    () => auditRows.filter((r) => r.organisation_id === tenant.id).slice(0, 6),
    [auditRows, tenant.id]
  );
  const enabledIntegrations = (integrationsData?.data ?? []).filter((i) => i.enabled).length;
  // The ledger query is a 200-row page, not a 30-day window, so on a busy
  // tenant these two totals are a floor. Say so rather than presenting a
  // partial sum as the period total.
  const ledgerCapped = ledger.length >= 200;

  return (
    <Stack gap="lg">
      {/* The row that answers "what is the shape of this tenant". A figure the
          page could not load is never a zero: each card carries its query's
          own loading state. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        <StatCard
          label={t('tenants.view.overview.kpi.users')}
          value={formatInteger(userCount)}
          hint={platformAdmins > 0 ? t(platformAdmins === 1 ? 'tenants.view.overview.kpi.platformAdminsOne' : 'tenants.view.overview.kpi.platformAdminsOther', { count: platformAdmins }) : t('tenants.view.overview.kpi.tenantScope')}
          icon={<UsersIcon />}
          tone="primary"
          loading={usersLoading}
          to={`/tenants/${tenant.id}?tab=users`}
          linkLabel={t('tenants.view.overview.link.users')}
        />
        <StatCard
          label={t('tenants.view.overview.kpi.spend')}
          value={formatMinor(debits30d, currency)}
          hint={ledgerCapped ? t('tenants.view.overview.kpi.ledgerCapped') : t('tenants.view.overview.kpi.ledgerEntries', { count: ledger.length })}
          icon={<DebitIcon />}
          tone="error"
          loading={ledgerLoading}
          to={`/tenants/${tenant.id}?tab=billing`}
          linkLabel={t('tenants.view.overview.link.ledger')}
        />
        <StatCard
          label={t('tenants.view.overview.kpi.topUps')}
          value={formatMinor(credits30d, currency)}
          hint={ledgerCapped ? t('tenants.view.overview.kpi.ledgerCapped') : t('tenants.view.overview.kpi.creditEntries')}
          icon={<CreditIcon />}
          tone="success"
          loading={ledgerLoading}
          to={`/tenants/${tenant.id}?tab=billing`}
          linkLabel={t('tenants.view.overview.link.ledger')}
        />
        <StatCard
          label={t('tenants.view.overview.kpi.integrations')}
          value={formatInteger(enabledIntegrations)}
          hint={t('tenants.view.overview.kpi.configured', { count: integrationsData?.data?.length ?? 0 })}
          icon={<PlugIcon />}
          tone="info"
          loading={integrationsLoading}
          to={`/tenants/${tenant.id}?tab=integrations`}
          linkLabel={t('tenants.view.overview.link.integrations')}
        />
      </div>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('tenants.view.overview.profile')}</Title>
          <Stack gap="sm">
            <Row label={t('tenants.columns.slug')} value={<code>{tenant.slug}</code>} />
            <Row label={t('tenants.columns.country')} value={tenant.country_iso} />
            <Row label={t('tenants.columns.timezone')} value={tenant.timezone} />
            <Row
              label={t('common.status')}
              value={
                <StatusBadge size="xs" status={tenant.is_active ? 'healthy' : 'inactive'}>
                  {tenant.is_active ? t('status.active') : t('status.inactive')}
                </StatusBadge>
              }
            />
            <Row label={t('common.created')} value={`${formatDate(tenant.created_at)} (${formatRelative(tenant.created_at)})`} />
          </Stack>
        </Stack>
      </Card>

      <AgreementsCard orgId={tenant.id} />
      <ImpersonationsCard tenantId={tenant.id} />

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('tenants.view.overview.latestActivity')}</Title>
          {auditLoading && recentActivity.length === 0 ? (
            <Stack gap="sm">
              <Skeleton height={18} />
              <Skeleton height={18} />
              <Skeleton height={18} />
            </Stack>
          ) : recentActivity.length === 0 ? (
            <Text fz="sm" c={MUTED}>{t('tenants.view.overview.noActivity')}</Text>
          ) : (
            <Stack gap="sm">
              {recentActivity.map((row) => (
                <Group key={`${row.source}-${row.id}`} gap="sm" align="baseline" wrap="nowrap">
                  <Badge size="xs" variant="outline">{t(`auditSource.${row.source}`, row.source)}</Badge>
                  <Text fz="sm" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.summary}
                  </Text>
                  <Text fz="xs" c={MUTED} style={{ whiteSpace: 'nowrap' }}>
                    {formatRelative(row.occurred_at)}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
          <Group justify="space-between" align="baseline" gap="sm" wrap="wrap">
            <Text fz="xs" c={MUTED}>{t('tenants.view.overview.auditHint')}</Text>
            <Link
              to={`/tenants/${tenant.id}?tab=audit`}
              style={{ color: 'var(--rr-color-primary)', fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}
            >
              {t('tenants.view.overview.viewAllActivity')} <span aria-hidden>→</span>
            </Link>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text fz="xs" c={MUTED}>{label}</Text>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}

/* The four icons for the metric row. They are decorative: every card says in
   words what it counts. */
function icon(children: React.ReactNode) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}

function UsersIcon() {
  return icon(<><circle cx="7" cy="6" r="2.6" /><path d="M2.5 15c0-2.5 2-4.2 4.5-4.2s4.5 1.7 4.5 4.2" /><path d="M12.5 4.2a2.6 2.6 0 0 1 0 4.6M15.5 15c0-2-.9-3.3-2.4-3.9" /></>);
}
function DebitIcon() {
  return icon(<><line x1="9" y1="3" x2="9" y2="14" /><polyline points="4.5,9.5 9,14 13.5,9.5" /></>);
}
function CreditIcon() {
  return icon(<><line x1="9" y1="15" x2="9" y2="4" /><polyline points="4.5,8.5 9,4 13.5,8.5" /></>);
}
function PlugIcon() {
  return icon(<><path d="M6 2.5v4M12 2.5v4" /><rect x="4" y="6.5" width="10" height="4.5" rx="1.6" /><path d="M9 11v4.5" /></>);
}
